import React, { useState, useEffect } from 'react';
import { useUser } from '../context/UserContext';
import Icon from '../components/Icon';

function Avatar({ avatar, name, size = 32 }) {
  if (avatar) return <img className="avatar" src={`https://sleepercdn.com/avatars/thumbs/${avatar}`} alt={name} style={{ width: size, height: size }} onError={e => e.target.style.display = 'none'} />;
  return <div className="avatar-fallback" style={{ width: size, height: size, fontSize: size * 0.32 }}>{name?.slice(0,2).toUpperCase() || '??'}</div>;
}

function MoveBadge({ change }) {
  if (change === 0) return <span style={{ color: 'var(--text3)', fontSize: '0.75rem' }}>—</span>;
  if (change > 0) return <span style={{ color: 'var(--green)', fontSize: '0.75rem', fontWeight: 600 }}>▲ {change}</span>;
  return <span style={{ color: 'var(--red)', fontSize: '0.75rem', fontWeight: 600 }}>▼ {Math.abs(change)}</span>;
}

const GRADE_COLORS = {
  'A+': '#00e676', 'A': '#00e676', 'A-': '#52d98a',
  'B+': 'var(--accent)', 'B': 'var(--accent)', 'B-': 'var(--accent)',
  'C+': '#ffd32a', 'C': '#ffd32a', 'C-': '#e8c244',
  'D': 'var(--accent2)', 'F': '#ff4757',
};
const POS_COLORS = { QB: 'var(--accent2)', RB: '#00e676', WR: 'var(--accent)', TE: '#ffd32a' };
const INJ_COLORS = { OUT: '#ff4757', IR: '#ff4757', DOUBT: 'var(--accent2)', Q: '#ffd32a', PUP: '#a855f7' };
const TIER_COLORS = { 1: '#00e676', 2: 'var(--accent)', 3: '#ffd32a', 4: 'var(--accent2)', 5: '#ff4757' };
const TIER_LABELS = { 1: 'Elite', 2: 'Solid', 3: 'Avg', 4: 'Weak', 5: 'Depth' };

function formatTime(ts) {
  if (!ts) return '';
  const diff = new Date() - new Date(ts);
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return `${Math.floor(diff / 86400000)}d ago`;
}

function GradeBox({ grade, size = 36 }) {
  const color = GRADE_COLORS[grade] || '#888';
  return (
    <div style={{
      width: size, height: size, borderRadius: 6,
      background: color + '22', color,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: 'var(--font-display)', fontSize: size * 0.34,
      border: `1px solid ${color}44`, flexShrink: 0,
    }}>{grade}</div>
  );
}

function PosGradePills({ posGrades }) {
  return (
    <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 6 }}>
      {['QB', 'RB', 'WR', 'TE'].map(pos => {
        const pg = posGrades?.[pos] || {};
        const grade = pg.grade || 'F';
        const tier = pg.best_tier || 5;
        const gradeColor = GRADE_COLORS[grade] || '#888';
        const tierColor = TIER_COLORS[tier] || '#888';
        return (
          <div key={pos} style={{
            display: 'flex', alignItems: 'center', gap: 4,
            background: 'var(--bg3)', border: '1px solid var(--border)',
            borderRadius: 6, padding: '3px 8px',
          }}>
            <span style={{ fontSize: '0.62rem', fontWeight: 700, color: POS_COLORS[pos], fontFamily: 'var(--font-mono)', minWidth: 20 }}>{pos}</span>
            <span style={{ fontSize: '0.7rem', fontWeight: 700, color: gradeColor }}>{grade}</span>
            <span style={{ fontSize: '0.58rem', color: tierColor, fontFamily: 'var(--font-mono)' }}>T{tier}</span>
          </div>
        );
      })}
    </div>
  );
}

function PosGradeDetail({ posGrades }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8, marginTop: 12 }}>
      {['QB', 'RB', 'WR', 'TE'].map(pos => {
        const pg = posGrades?.[pos] || {};
        const grade = pg.grade || 'F';
        const gradeColor = GRADE_COLORS[grade] || '#888';
        const players = pg.players || [];
        return (
          <div key={pos} style={{
            background: 'var(--bg3)', border: `1px solid ${gradeColor}33`,
            borderRadius: 8, padding: '10px 12px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ fontSize: '0.72rem', fontWeight: 700, color: POS_COLORS[pos], fontFamily: 'var(--font-mono)' }}>{pos}</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: '0.65rem', color: 'var(--text3)', fontFamily: 'var(--font-mono)' }}>
                  Avg T{pg.starter_avg_tier?.toFixed(1)}
                </span>
                <GradeBox grade={grade} size={26} />
              </div>
            </div>
            {players.slice(0, 3).map((p, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                <span style={{
                  fontSize: '0.6rem', fontWeight: 700, padding: '1px 5px', borderRadius: 3,
                  background: (TIER_COLORS[p.tier] || '#888') + '22',
                  color: TIER_COLORS[p.tier] || '#888',
                  fontFamily: 'var(--font-mono)', minWidth: 20, textAlign: 'center',
                }}>T{p.tier}</span>
                <span style={{ fontSize: '0.75rem', color: 'var(--text)', fontWeight: 500 }}>{p.name}</span>
                <span style={{ fontSize: '0.65rem', color: 'var(--text3)', fontFamily: 'var(--font-mono)', marginLeft: 'auto' }}>#{p.rank}</span>
                {p.injury_status && (
                  <span style={{ fontSize: '0.58rem', color: INJ_COLORS[p.injury_status] || 'var(--red)', fontWeight: 700 }}>
                    {p.injury_status}
                  </span>
                )}
              </div>
            ))}
            {players.length === 0 && (
              <div style={{ fontSize: '0.72rem', color: 'var(--red)', fontStyle: 'italic' }}>No ranked players</div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function PowerRankings({ api }) {
  const { currentUser } = useUser();
  const [rankings, setRankings] = useState([]);
  const [health, setHealth] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [activeTab, setActiveTab] = useState('rankings');
  const [expandedTeam, setExpandedTeam] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [r, h, t] = await Promise.all([
          fetch(`${api}/api/power-rankings`).then(r => r.json()),
          fetch(`${api}/api/roster-health`).then(r => r.json()),
          fetch(`${api}/api/transactions`).then(r => r.json()),
        ]);
        setRankings(Array.isArray(r) ? r : []);
        setHealth(Array.isArray(h) ? h : []);
        setTransactions(Array.isArray(t) ? t : []);
        setError(false);
      } catch (e) { console.error(e); setError(true); }
      setLoading(false);
    };
    load();
  }, [api]);

  if (loading) return <div className="loading"><div className="spinner" /><span>Loading rankings...</span></div>;

  if (error) return (
    <div className="empty-state">
      <div style={{ marginBottom: 12 }}><Icon emoji="⚠️" size={28} /></div>
      <p>Could not load rankings. Check your connection and refresh.</p>
    </div>
  );

  const tabs = [
    { key: 'rankings',     label: 'Power Rankings', icon: '⚡' },
    { key: 'health',       label: 'Roster Health',  icon: '🏥' },
    { key: 'transactions', label: 'Transactions',   icon: '🔄' },
  ];

  const maxScore = rankings.length ? Math.max(...rankings.map(d => d.power_score)) : 1;
  const seasonMode = rankings[0]?.season_mode;

  return (
    <div>
      <div className="page-header">
        <div className="page-title">Power Rankings</div>
        <div className="page-subtitle">
          Powered by FantasyPros dynasty consensus · {seasonMode === 'preseason' ? '60% Roster Quality · 25% History · Preseason Mode' : '35% Roster · 20% Record · 20% Form · In-Season Mode'}
        </div>
      </div>

      <div className="tab-bar">
        {tabs.map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)} className={`tab-btn${activeTab === t.key ? ' active' : ''}`}>
            <Icon emoji={t.icon} size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
            {t.label}
          </button>
        ))}
      </div>

      {/* ── POWER RANKINGS TAB ── */}
      {activeTab === 'rankings' && (
        <>
          {/* Mode banner */}
          <div className="card" style={{
            background: seasonMode === 'preseason'
              ? 'linear-gradient(135deg, rgba(var(--accent2-rgb),0.08), rgba(var(--accent-rgb),0.05))'
              : 'linear-gradient(135deg, rgba(var(--accent-rgb),0.06), rgba(var(--accent2-rgb),0.04))',
            borderColor: seasonMode === 'preseason' ? 'rgba(var(--accent2-rgb),0.3)' : 'rgba(var(--accent-rgb),0.2)',
            marginBottom: 16,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {seasonMode === 'preseason'
                ? <Icon emoji="🏕️" size={20} />
                : <span style={{ fontSize: '1.2rem' }}>🏈</span>}
              <div>
                <div style={{ fontSize: '0.72rem', color: 'var(--text3)', fontFamily: 'var(--font-mono)', letterSpacing: 1, textTransform: 'uppercase' }}>
                  {seasonMode === 'preseason' ? 'Preseason Mode' : 'In-Season Mode'}
                </div>
                <div style={{ fontSize: '0.8rem', color: 'var(--text2)', marginTop: 2 }}>
                  {seasonMode === 'preseason'
                    ? 'Rankings driven by FantasyPros dynasty roster tiers (60%) + all-time history (25%). Switches to in-season formula once week 3 begins.'
                    : 'Balancing FantasyPros roster quality (35%) with current record, points scored, and recent form.'}
                </div>
              </div>
            </div>
          </div>

          {!rankings.length ? (
            <div className="empty-state"><p>Rankings loading — expert data takes ~2 min on first start.</p></div>
          ) : (
            <div className="card">
              {rankings.map((team, i) => {
                const isMe = currentUser && team.roster_id === currentUser.roster_id;
                const isExpanded = expandedTeam === team.roster_id;
                return (
                  <div key={team.roster_id}>
                    <div
                      onClick={() => setExpandedTeam(isExpanded ? null : team.roster_id)}
                      style={{
                        display: 'grid', gridTemplateColumns: '54px 48px 1fr auto',
                        gap: 14, alignItems: 'center', padding: '14px 8px',
                        borderBottom: (!isExpanded && i < rankings.length - 1) ? '1px solid var(--border)' : 'none',
                        background: isMe ? 'rgba(var(--accent-rgb),0.04)' : 'transparent',
                        borderRadius: isMe ? 8 : 0,
                        outline: isMe ? '1px solid rgba(var(--accent-rgb),0.15)' : 'none',
                        cursor: 'pointer',
                      }}>
                      {/* Rank */}
                      <div style={{ textAlign: 'center' }}>
                        <div style={{
                          fontFamily: 'var(--font-display)', fontSize: '2rem', lineHeight: 1,
                          color: i === 0 ? 'var(--yellow)' : i < 3 ? 'var(--accent)' : 'var(--text2)',
                        }}>{team.power_rank}</div>
                        <MoveBadge change={team.rank_change} />
                      </div>

                      <Avatar avatar={team.avatar} name={team.display_name} size={44} />

                      {/* Team info */}
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2, flexWrap: 'wrap' }}>
                          {team.display_name}
                          {isMe && <span style={{ fontSize: '0.58rem', color: 'var(--accent)', fontFamily: 'var(--font-mono)', letterSpacing: '1px' }}>YOU</span>}
                          {i === 0 && <span style={{ fontSize: '0.68rem', background: 'rgba(255,211,42,0.15)', color: 'var(--yellow)', padding: '1px 8px', borderRadius: 10, display: 'inline-flex', alignItems: 'center', gap: 4 }}><Icon emoji="👑" size={12} />#1</span>}
                        </div>
                        <div style={{ fontSize: '0.72rem', color: 'var(--text3)', fontFamily: 'var(--font-mono)', display: 'flex', gap: 10 }}>
                          <span>{team.wins}-{team.losses}</span>
                          <span>·</span>
                          <span>PF {team.pf?.toFixed(1)}</span>
                          {team.recent_margin !== 0 && (
                            <>
                              <span>·</span>
                              <span style={{ color: team.recent_margin > 0 ? 'var(--green)' : 'var(--red)' }}>
                                Recent {team.recent_margin > 0 ? '+' : ''}{team.recent_margin}
                              </span>
                            </>
                          )}
                        </div>
                        {team.pos_grades && <PosGradePills posGrades={team.pos_grades} />}
                        <div style={{ height: 3, background: 'var(--bg4)', borderRadius: 2, marginTop: 8, overflow: 'hidden' }}>
                          <div style={{
                            height: '100%', width: `${(team.power_score / maxScore) * 100}%`,
                            background: i === 0
                              ? 'linear-gradient(90deg, var(--yellow), var(--accent2))'
                              : 'linear-gradient(90deg, var(--accent), var(--green))',
                            borderRadius: 2, transition: 'width 0.8s ease',
                          }} />
                        </div>
                      </div>

                      {/* Score + grade */}
                      <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
                        <div>
                          <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.4rem', color: 'var(--text)', lineHeight: 1 }}>{team.power_score}</div>
                          <div style={{ fontSize: '0.6rem', color: 'var(--text3)', fontFamily: 'var(--font-mono)', letterSpacing: 1 }}>POWER</div>
                        </div>
                        {team.overall_grade && <GradeBox grade={team.overall_grade} size={30} />}
                      </div>
                    </div>

                    {/* Expanded positional breakdown */}
                    {isExpanded && (
                      <div style={{ padding: '0 8px 16px', borderBottom: i < rankings.length - 1 ? '1px solid var(--border)' : 'none' }}>
                        <div style={{ fontSize: '0.65rem', color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: 1.5, fontFamily: 'var(--font-mono)', marginBottom: 4 }}>
                          FantasyPros Dynasty Tier Breakdown
                        </div>
                        <PosGradeDetail posGrades={team.pos_grades} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* ── ROSTER HEALTH TAB ── */}
      {activeTab === 'health' && (
        <>
          {!health.length ? (
            <div className="empty-state">
              <p>Roster health loads after FantasyPros data is fetched (~2 min on first start).</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {health.map((team) => {
                const isExpanded = expandedTeam === team.roster_id;
                const isMe = currentUser && team.roster_id === currentUser.roster_id;
                return (
                  <div key={team.roster_id} className="card" style={{
                    cursor: 'pointer',
                    borderColor: isMe ? 'rgba(var(--accent-rgb),0.4)' : isExpanded ? 'var(--accent)' : 'var(--border)',
                    background: isMe ? 'rgba(var(--accent-rgb),0.03)' : undefined,
                  }} onClick={() => setExpandedTeam(isExpanded ? null : team.roster_id)}>

                    <div style={{ display: 'grid', gridTemplateColumns: '36px 1fr auto auto', gap: 12, alignItems: 'center' }}>
                      <Avatar avatar={team.avatar} name={team.display_name} size={36} />
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: '0.92rem', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                          {team.display_name}
                          {isMe && <span style={{ fontSize: '0.6rem', color: 'var(--accent)', fontFamily: 'var(--font-mono)' }}>YOU</span>}
                        </div>
                        <div style={{ fontSize: '0.72rem', color: 'var(--text3)', fontFamily: 'var(--font-mono)' }}>
                          #{team.rank} standings · {team.wins}-{team.losses}
                          {team.injured_players?.length > 0 && (
                            <span style={{ marginLeft: 8, color: 'var(--red)', display: 'inline-flex', alignItems: 'center', gap: 4 }}><Icon emoji="⚠️" size={12} />{team.injured_players.length} injured</span>
                          )}
                        </div>
                        {team.pos_grades && <PosGradePills posGrades={team.pos_grades} />}
                      </div>

                      <div style={{ width: 90 }}>
                        <div style={{ height: 6, background: 'var(--bg4)', borderRadius: 3, overflow: 'hidden' }}>
                          <div style={{
                            height: '100%', width: `${team.health_score}%`,
                            background: team.health_score >= 80 ? 'var(--green)' : team.health_score >= 60 ? 'var(--accent)' : team.health_score >= 40 ? 'var(--yellow)' : 'var(--red)',
                            borderRadius: 3, transition: 'width 0.6s ease',
                          }} />
                        </div>
                        <div style={{ fontSize: '0.62rem', color: 'var(--text3)', fontFamily: 'var(--font-mono)', marginTop: 2, textAlign: 'right' }}>
                          {team.health_score}/100
                        </div>
                      </div>

                      <GradeBox grade={team.grade} size={36} />
                    </div>

                    {isExpanded && (
                      <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
                        <PosGradeDetail posGrades={team.pos_grades} />

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 16 }}>
                          <div>
                            <div style={{ fontSize: '0.68rem', color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 8, fontFamily: 'var(--font-mono)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                              <Icon emoji="🎯" size={13} />Top Needs
                            </div>
                            {team.team_needs?.length === 0 ? (
                              <div style={{ fontSize: '0.82rem', color: 'var(--text3)' }}>No major weaknesses detected</div>
                            ) : (
                              team.team_needs?.map((need, j) => (
                                <div key={j} style={{
                                  display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6,
                                  padding: '6px 10px', background: 'var(--bg3)', borderRadius: 6,
                                }}>
                                  <span style={{
                                    width: 28, height: 22, background: (POS_COLORS[need.position] || 'var(--text3)') + '22',
                                    color: POS_COLORS[need.position] || 'var(--text3)',
                                    borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: '0.65rem', fontWeight: 700, fontFamily: 'var(--font-mono)', flexShrink: 0,
                                  }}>{need.position}</span>
                                  <span style={{ fontSize: '0.78rem', color: 'var(--text2)' }}>{need.reason}</span>
                                </div>
                              ))
                            )}
                          </div>

                          <div>
                            <div style={{ fontSize: '0.68rem', color: 'var(--red)', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 8, fontFamily: 'var(--font-mono)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                              <Icon emoji="🏥" size={13} />Injury Report
                            </div>
                            {team.injured_players?.length === 0 ? (
                              <div style={{ fontSize: '0.82rem', color: 'var(--green)', display: 'flex', alignItems: 'center', gap: 6 }}><Icon emoji="✅" size={14} />No injuries reported</div>
                            ) : (
                              team.injured_players?.map((p, j) => (
                                <div key={j} style={{
                                  display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6,
                                  padding: '6px 10px', background: 'var(--bg3)', borderRadius: 6,
                                }}>
                                  <span style={{
                                    padding: '1px 6px', borderRadius: 4, fontSize: '0.62rem', fontWeight: 700,
                                    fontFamily: 'var(--font-mono)', flexShrink: 0,
                                    background: (INJ_COLORS[p.status] || 'var(--text3)') + '22',
                                    color: INJ_COLORS[p.status] || 'var(--text3)',
                                  }}>{p.status}</span>
                                  <span style={{ fontSize: '0.78rem', fontWeight: 600 }}>{p.name}</span>
                                  <span style={{ fontSize: '0.7rem', color: 'var(--text3)', fontFamily: 'var(--font-mono)' }}>{p.position} · {p.team}</span>
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    <div style={{ textAlign: 'center', marginTop: 8, fontSize: '0.65rem', color: 'var(--text3)' }}>
                      {isExpanded ? '▲ collapse' : '▼ expand for tier breakdown & injuries'}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* ── TRANSACTIONS TAB ── */}
      {activeTab === 'transactions' && (
        <>
          {!transactions.length ? (
            <div className="empty-state">
              <div style={{ marginBottom: 12 }}><Icon emoji="🔄" size={28} /></div>
              <p>No recent transactions found. Transactions appear during the active season.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {transactions.map((t, i) => {
                const isTrade = t.type === 'trade';
                const typeIcon = isTrade ? '🔄' : t.type === 'waiver' ? '📋' : '✅';
                const typeText = isTrade ? 'TRADE' : t.type === 'waiver' ? 'WAIVER' : 'FREE AGENT';
                const typeColor = isTrade ? 'var(--accent)' : t.type === 'waiver' ? 'var(--yellow)' : 'var(--green)';
                const adds = Object.values(t.adds || {});
                const drops = Object.values(t.drops || {});

                return (
                  <div key={t.transaction_id || i} className="card" style={{ padding: '14px 16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{
                          fontSize: '0.65rem', fontWeight: 700, letterSpacing: 1.5,
                          color: typeColor, background: typeColor + '18',
                          padding: '2px 8px', borderRadius: 4, fontFamily: 'var(--font-mono)',
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                        }}><Icon emoji={typeIcon} size={12} />{typeText}</span>
                        <span style={{ fontSize: '0.78rem', color: 'var(--text2)', fontWeight: 500 }}>
                          {t.managers.join(isTrade ? ' ⇄ ' : ' · ')}
                        </span>
                      </div>
                      <span style={{ fontSize: '0.7rem', color: 'var(--text3)', fontFamily: 'var(--font-mono)' }}>
                        {formatTime(t.status_updated)}
                      </span>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: adds.length && drops.length ? '1fr 1fr' : '1fr', gap: 10 }}>
                      {adds.length > 0 && (
                        <div>
                          <div style={{ fontSize: '0.65rem', color: 'var(--green)', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 6, fontFamily: 'var(--font-mono)', display: 'flex', alignItems: 'center', gap: 4 }}><Icon emoji="➕" size={12} />Added</div>
                          {adds.map((p, j) => (
                            <div key={j} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                              <span style={{
                                width: 26, height: 20, background: (POS_COLORS[p.position] || '#888') + '22',
                                color: POS_COLORS[p.position] || '#888',
                                borderRadius: 3, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: '0.6rem', fontWeight: 700, fontFamily: 'var(--font-mono)', flexShrink: 0,
                              }}>{p.position}</span>
                              <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>{p.name}</span>
                              <span style={{ fontSize: '0.7rem', color: 'var(--text3)' }}>{p.team}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {drops.length > 0 && (
                        <div>
                          <div style={{ fontSize: '0.65rem', color: 'var(--red)', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 6, fontFamily: 'var(--font-mono)', display: 'flex', alignItems: 'center', gap: 4 }}><Icon emoji="➖" size={12} />Dropped</div>
                          {drops.map((p, j) => (
                            <div key={j} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                              <span style={{
                                width: 26, height: 20, background: (POS_COLORS[p.position] || '#888') + '22',
                                color: POS_COLORS[p.position] || '#888',
                                borderRadius: 3, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: '0.6rem', fontWeight: 700, fontFamily: 'var(--font-mono)', flexShrink: 0,
                              }}>{p.position}</span>
                              <span style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text2)' }}>{p.name}</span>
                              <span style={{ fontSize: '0.7rem', color: 'var(--text3)' }}>{p.team}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {isTrade && t.trade_info?.draft_picks?.length > 0 && (
                      <div style={{ marginTop: 8, padding: '6px 10px', background: 'rgba(var(--accent-rgb),0.06)', borderRadius: 6, fontSize: '0.78rem', color: 'var(--accent)', display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Icon emoji="🎯" size={14} />Draft picks: {t.trade_info.draft_picks.map(p => `${p.season} Round ${p.round}`).join(', ')}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
